import clsx from "clsx";
import { useEffect, useRef, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { Map, Marker } from "react-map-gl";
import "mapbox-gl/dist/mapbox-gl.css";

import config from "../../config";
import useViewport, { Viewport } from "../../hooks/useViewport";
import { getLocationBySlug } from "../../lib/locations";
import { getSEOPageProps } from "../../lib/seo";

import IconBike from "../../assets/icons/icon-bike.svg";
import IconCar from "../../assets/icons/icon-car.svg";
import IconTram from "../../assets/icons/icon-tram.svg";
import IconDocument from "../../assets/icons/icon-document.svg";

import SeoMeta from "../../components/seo-meta";
import AddressLine from "../../components/address-line";
import BookingForm from "../../components/booking-form";
import Button from "../../components/button";
import Heading from "../../components/heading";
import Gallery from "../../components/gallery";
import OptionListItem from "../../components/option-list-item";
import MeetOurClients from "../../components/meet-our-clients";
import LocationSlider from "../../components/location-slider";
import LocationListItem from "../../components/location-list-item";
import LocationAmenitiesModal from "../../components/location-amenities-modal";
import LocationReviews from "../../components/location-reviews";

import styles from "./location.module.scss";

const LocationHeader = ({ location }) => {
  const device = useViewport();

  return (
    <div className={styles.LocationHeader}>
      <div className="">
        <h1>{location.name}</h1>
        {device === Viewport.PHONE && location?.document && (
          <a href={location.document.url} target="_blank" rel="noreferrer">
            <div className="my-1 flex flex-row items-center justify-start">
              <IconDocument role="presentation" className="mr-2" />
              <p className="font-book">Open Building Brochure</p>
            </div>
          </a>
        )}
      </div>
      <div className={styles.LocationAddress}>
        <p>
          <b>
            <AddressLine location={location} />
          </b>
        </p>
        {location?.document && (
          <a href={location.document.url} target="_blank" rel="noreferrer">
            <div className="my-1 flex flex-row items-center justify-start">
              <IconDocument role="presentation" className="mr-2" />
              <p className="font-book">Open Building Brochure</p>
            </div>
          </a>
        )}
      </div>
    </div>
  );
};

const SolutionOptions = ({ locationSolution }) => {
  const { solution, optionsCollection } = locationSolution;

  return (
    <div className={styles.SolutionOptions}>
      <div className={styles.SolutionOptionsHeading}>
        <h3>{solution.name} Solutions</h3>
      </div>
      <div className={styles.SolutionOptionsList}>
        {optionsCollection?.items?.map((option) => (
          <OptionListItem key={option.slug} option={option} />
        ))}
      </div>
    </div>
  );
};

const Directions = ({ icon, text }) => {
  return (
    <div className="my-4 flex flex-row items-center justify-start gap-5">
      <div className={styles.DirectionIcon}>{icon}</div>
      <div className={styles.Directions}>{text}</div>
    </div>
  );
};

const GetInTouch = () => (
  <div className={styles.Contact}>
    <div>
      <b>Want to get in touch now?</b>
    </div>
    <div>
      Schedule a visit above. Or, call us at{" "}
      <a href="tel:+14166285333" className="text-link">
        (416) 628-5333
      </a>
    </div>
  </div>
);

const navbarOffset = 60;

export default function LocationDetailsPage({ location, seo }) {
  const [showAmenitiesModal, setShowAmenitiesModal] = useState(false);
  const [isSticky, setIsSticky] = useState(false);
  const [topPosition, setTopPosition] = useState(0);
  const device = useViewport();

  const stickyFormContainerRef = useRef();
  const stickyFormRef = useRef();

  useEffect(() => {
    // on scroll.
    function handleStickyScroll() {
      const bBox = stickyFormContainerRef.current.getBoundingClientRect();
      const stickyFormBbox = stickyFormRef.current.getBoundingClientRect();

      const containerTopInView = bBox.top >= navbarOffset;
      const containerBottomInView =
        bBox.top + bBox.height - navbarOffset <= stickyFormBbox.height;

      if (!containerTopInView && !containerBottomInView) {
        setTopPosition(0);
        setIsSticky(true);
      } else {
        if (containerBottomInView) {
          // At the bottom, set the top position.
          setTopPosition(bBox.height - stickyFormBbox.height);
        } else {
          setTopPosition(0);
        }
        setIsSticky(false);
      }
    }

    window.addEventListener("scroll", handleStickyScroll);

    return () => window.removeEventListener("scroll", handleStickyScroll);
  }, [stickyFormContainerRef, stickyFormRef, isSticky]);

  return (
    <>
      <div className="container">
        <SeoMeta {...seo} />
        <LocationHeader location={location} />

        <div
          className={styles.LocationPageContainer}
          ref={stickyFormContainerRef}
        >
          <div className={styles.LocationPageContent}>
            {device == Viewport.PHONE && (
              <div className={styles.LocationDescription}>
                <p>{location.description}</p>
              </div>
            )}

            {location.gallery && (
              <Gallery media={location.gallery.galleryItemsCollection.items} />
            )}
            <div className={styles.LocationDescription}>
              {device !== Viewport.PHONE && <p>{location.description}</p>}

              <p className={styles.SecondaryDescription}>
                {location.secondaryDescription}
              </p>
            </div>

            {location.locationSolutionsCollection.items.map(
              (locationSolution) => (
                <section key={locationSolution.solution.name}>
                  <SolutionOptions locationSolution={locationSolution} />
                </section>
              )
            )}

            <section>
              <Heading small>Amenities</Heading>

              <div className={styles.AmenityRow}>
                {location.featuredAmenitiesCollection.items.map(
                  (amenity, index) => (
                    <div
                      key={`amenity-${index}`}
                      className={styles.AmenityItem}
                    >
                      <Image
                        src={amenity.image.url}
                        width="184"
                        height="120"
                        objectFit="cover"
                        alt="step1"
                      />
                      <p>{amenity.title}</p>
                    </div>
                  )
                )}
              </div>

              <div className="my-10 text-center md:text-left">
                <Button
                  outline
                  rounded
                  small
                  onClick={() => setShowAmenitiesModal(true)}
                >
                  View All Amenities
                </Button>
              </div>
            </section>

            <section>
              <Heading small>Location</Heading>

              <p className="my-10">
                <b>{location.address1}</b>
                <br />
                <AddressLine location={location} />
              </p>

              <div className={styles.Map}>
                <Map
                  initialViewState={{
                    latitude: parseFloat(location.latitude),
                    longitude: parseFloat(location.longitude),
                    zoom: 15
                  }}
                  attributionControl={false}
                  mapStyle={config.MAPBOX_STYLE}
                  mapboxAccessToken={config.MAPBOX_TOKEN}
                >
                  <Marker
                    key={location.slug}
                    longitude={location.longitude}
                    latitude={location.latitude}
                    anchor="bottom"
                  >
                    <img
                      alt=""
                      src="/images/map-marker.svg"
                      width="25"
                      height="25"
                    />
                  </Marker>
                </Map>
              </div>

              <div className="mt-10">
                {location.transitDirections && (
                  <Directions
                    icon={<IconTram role="presentation" width="29" />}
                    text={location.transitDirections}
                  />
                )}
                {location.bikewalkDirections && (
                  <Directions
                    icon={<IconBike role="presentation" width="29" />}
                    text={location.bikewalkDirections}
                  />
                )}
                {location.drivingDirections && (
                  <Directions
                    icon={<IconCar role="presentation" width="29" />}
                    text={location.drivingDirections}
                  />
                )}
              </div>
            </section>

            <section className={styles.LocationReviews}>
              <LocationReviews location={location} />
            </section>

            {location.communityManager && (
              <section className={styles.CommunityManagerSection}>
                <Heading small>Community Manager</Heading>

                <div className={styles.CommunityManagerContent}>
                  <div className={styles.CommunityManagerAvatarWrapper}>
                    <Image
                      className={styles.CommunityManagerAvatar}
                      src={location.communityManager.avatar.url}
                      alt={location.communityManager.name}
                      width="195"
                      height="195"
                      layout="responsive"
                      objectFit="cover"
                    />
                  </div>

                  <div className={styles.CommunityManager}>
                    <h4 className="mb-5">{location.communityManager.name}</h4>
                    <p>{location.communityManager.biography}</p>
                  </div>
                </div>
              </section>
            )}

            {location?.nearbyLocationsCollection?.items?.length > 0 && (
              <section>
                <div className="container">
                  <div className={styles.Locations}>
                    <Heading small>Nearby Locations</Heading>
                    {device === Viewport.PHONE && (
                      <LocationSlider locations={location.nearbyLocationsCollection.items} />
                    )}
                    {device !== Viewport.PHONE && (
                      <div className={styles.LocationList}>
                        {location?.nearbyLocationsCollection?.items?.map(
                          (loc) => (
                            <LocationListItem
                              large
                              key={loc.slug}
                              location={loc}
                            />
                          )
                        )}
                      </div>
                    )}
                    <Link href="/locations" passHref>
                      <a>
                        <Button outline rounded small>
                          View All Locations
                        </Button>
                      </a>
                    </Link>
                  </div>
                </div>
              </section>
            )}
          </div>
          <div
            className={clsx(
              styles.FormScrollContainer,
              isSticky && styles.StickForm
            )}
          >
            <div
              className={styles.FormWrapper}
              style={{ top: `${topPosition}px` }}
            >
              <div
                className={clsx(isSticky && styles.FormFixed)}
                ref={stickyFormRef}
              >
                <div className={styles.StickyForm}>
                  <BookingForm
                    title="Interested in this location?"
                    location={location}
                  />
                </div>

                <GetInTouch />
              </div>
            </div>
          </div>
        </div>

        <MeetOurClients />
      </div>

      <LocationAmenitiesModal
        isOpen={showAmenitiesModal}
        location={location}
        onRequestClose={() => setShowAmenitiesModal(false)}
      />

      <div className={styles.MobileBookingForm}>
        <BookingForm title="Interested in this location?" location={location} />
      </div>
    </>
  );
}

export async function getServerSideProps(context) {
  const { params } = context;
  try {
    const [location, seo] = await Promise.all([
      getLocationBySlug(params.slug),
      getSEOPageProps(context)
    ]);

    return {
      props: { location, seo }
    };
  } catch (e) {
    console.log(e.message);
  }
}
